import cryptoRandomString from "crypto-random-string";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsByFormId,
  getSubmission,
  handleAddFieldTableRow,
  handleDeleteFieldTableRow,
  handleEditFieldTableRow,
  handleFieldTableChange,
  handleFormChange,
  handleSaveFieldTableRow,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldLabel,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FieldTable,
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
  FormTextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getDiagnosisDetails, getMedications } from "../services/soar";
import { getSchema } from "./joi/validation";

function KYHFPlanOfCarev3() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await getCareTeams(clientid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setCareTeams(response);
      setLoadStatus((ls) => ({ ...ls, submissionsLoaded: true }));
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        const formSubmissionsResponse = await getFormsySubmissionsByFormId(
          response.FormsyDetails.FormId,
          clientid
        );
        setAllFormSubmissions(formSubmissionsResponse);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow, unshift = false) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow, unshift);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  useEffect(() => {
    if (!loadStatus.configLoaded || !loadStatus.dataSet) {
      return;
    }

    setData((d) => {
      const obj = { ...d };
      obj.YouthName["@value"] = clientData.client.fullName;
      obj.DateOfBirth["@value"] = getFieldDate(clientData.client.dob);
      return obj;
    });
  }, [loadStatus.configLoaded, loadStatus.dataSet, clientData]);

  useEffect(() => {
    const controller = new AbortController();

    if (!loadStatus.dxRecordsProcessed && loadStatus.dataSet) {
      const loadDiagnoses = async () => {
        const clientDx = await getDiagnosisDetails(
          getFieldValue(data.SelectedProgram),
          controller.signal
        );

        if (!controller.signal.aborted && clientDx?.length > 0) {
          setData((d) => {
            const obj = { ...d };
            obj.Diagnosis["@value"] = clientDx
              .map((dx) => `${dx["dx.ccx_code"]} - ${dx["dx.ccx_name"]}`)
              .join("; ");
            return obj;
          });
          setLoadStatus((ls) => {
            return { ...ls, dxRecordsProcessed: true };
          });
        }
      };

      loadDiagnoses();
    }

    return () => {
      controller.abort();
    };
  }, [loadStatus.dataSet, data.SelectedProgram, loadStatus.dxRecordsProcessed]);

  useEffect(() => {
    const controller = new AbortController();

    if (loadStatus.medicationsProcessed || !loadStatus.dataSet) {
      return;
    }

    const loadRecords = async () => {
      const response = await getMedications(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const today = moment();
      const currentMeds = response.filter((med) => {
        const startDate = moment(getFieldDate(med.ccx_startdate));

        if (startDate.isAfter(today)) {
          return false;
        }

        if (med.ccx_stopdate === undefined) {
          return true;
        }

        const endDate = moment(getFieldDate(med.ccx_stopdate));

        if (endDate.isBefore(today)) {
          return false;
        }

        return true;
      });

      if (currentMeds.length > 0) {
        setData((d) => {
          const obj = { ...d };
          obj.Medications["@value"] = currentMeds
            .map((med) => med.ccx_brandname)
            .join(", ");
          return obj;
        });
      }

      setLoadStatus((ls) => ({ ...ls, medicationsProcessed: true }));
    };

    loadRecords();
    return () => {
      controller.abort();
    };
  }, [clientid, loadStatus.dataSet, loadStatus.medicationsProcessed]);

  const pastSubmissionsForProgram = useMemo(() => {
    if (!loadStatus.submissionsLoaded || !loadStatus.dataSet) {
      return [];
    }

    if (
      formsyDetails.FinalSigned === "1" &&
      !getFieldValue(formsyDetails.FormResults.results.DateCompleted)
    ) {
      return []; // this is the Initial Crisis Plan
    }

    const pastSubmissions = allFormSubmissions.filter(
      (s) =>
        s.FinalSigned === "1" &&
        s.Active === "1" &&
        `{${s.ClientProgramId}}` === getFieldValue(data.SelectedProgram) &&
        s.SubmissionId !== formsyDetails.SubmissionId
    );
    return pastSubmissions;
  }, [
    loadStatus.submissionsLoaded,
    loadStatus.dataSet,
    allFormSubmissions,
    formsyDetails,
    data.SelectedProgram,
  ]);

  const reviewSubmissions = useMemo(() => {
    const pastReviews = pastSubmissionsForProgram.filter(
      (s) => getFieldValue(s.FormResults.results.PlanType) === "Review"
    );
    return pastReviews;
  }, [pastSubmissionsForProgram]);

  const renderFamilyVisionRatings = useCallback(
    (item) => {
      if (reviewSubmissions.length > 0) {
        const pastRatings = (
          <>
            <div className="fw-bold">Past Ratings:</div>
            {reviewSubmissions
              .filter((sub) => {
                const results = sub.FormResults.results;
                const familyVision = JSON.parse(
                  getFieldValue(results.FamilyVision)
                );
                const pastVision = familyVision.filter(
                  (f) => f.Vision === item.Vision
                )[0];

                return pastVision !== undefined;
              })
              .map((sub) => {
                const results = sub.FormResults.results;
                const dateCompleted = getFieldValue(results.DateCompleted);
                const familyVision = JSON.parse(
                  getFieldValue(results.FamilyVision)
                );
                const pastVision = familyVision.filter(
                  (f) => f.Vision === item.Vision
                )[0];

                return (
                  <div key={sub.SubmissionId}>
                    {moment(dateCompleted).format("l")}: {pastVision.Rating}
                  </div>
                );
              })}
          </>
        );

        return pastRatings;
      }

      return null;
    },
    [reviewSubmissions]
  );

  const renderTeamMissionRatings = useCallback(
    (item) => {
      if (reviewSubmissions.length > 0) {
        const pastRatings = (
          <>
            <div className="fw-bold">Past Ratings:</div>
            {reviewSubmissions
              .filter((sub) => {
                const results = sub.FormResults.results;
                const teamMission = JSON.parse(
                  getFieldValue(results.TeamMission)
                );
                const pastMission = teamMission.filter(
                  (f) => f.Mission === item.Mission
                )[0];

                return pastMission !== undefined;
              })
              .map((sub) => {
                const results = sub.FormResults.results;
                const dateCompleted = getFieldValue(results.DateCompleted);
                const teamMission = JSON.parse(
                  getFieldValue(results.TeamMission)
                );
                const pastMission = teamMission.filter(
                  (f) => f.Mission === item.Mission
                )[0];

                return (
                  <div key={sub.SubmissionId}>
                    {moment(dateCompleted).format("l")}: {pastMission.Rating}
                  </div>
                );
              })}
          </>
        );

        return pastRatings;
      }

      return null;
    },
    [reviewSubmissions]
  );

  const renderPastNeedProgressRating = useCallback(
    (item) => {
      if (reviewSubmissions.length > 0) {
        const pastRatings = (
          <>
            <div className="fw-bold">Past Ratings:</div>
            {reviewSubmissions
              .filter((sub) => {
                const results = sub.FormResults.results;
                const needsStrategies = JSON.parse(
                  getFieldValue(results.NeedsStrategies)
                );
                const pastNeed = needsStrategies.filter(
                  (f) => f.NeedsStatement === item.NeedsStatement
                )[0];
                return pastNeed !== undefined;
              })
              .map((sub) => {
                const results = sub.FormResults.results;
                const dateCompleted = getFieldValue(results.DateCompleted);
                const needsStrategies = JSON.parse(
                  getFieldValue(results.NeedsStrategies)
                );
                const pastNeed = needsStrategies.filter(
                  (f) => f.NeedsStatement === item.NeedsStatement
                )[0];

                return (
                  <div key={sub.SubmissionId}>
                    {moment(dateCompleted).format("l")}: {pastNeed.NeedProgress}
                  </div>
                );
              })}
          </>
        );

        return pastRatings;
      }

      return null;
    },
    [reviewSubmissions]
  );

  const renderPastNeedsOutcomeRating = useCallback(
    (item) => {
      if (reviewSubmissions.length > 0) {
        const pastRatings = (
          <>
            <div className="fw-bold">Past Ratings:</div>
            {reviewSubmissions
              .filter((sub) => {
                const results = sub.FormResults.results;
                const needsStrategies = JSON.parse(
                  getFieldValue(results.NeedsStrategies)
                );
                const pastNeed = needsStrategies.filter(
                  (f) => f.NeedsStatement === item.NeedsStatement
                )[0];

                return pastNeed !== undefined;
              })
              .map((sub) => {
                const results = sub.FormResults.results;
                const dateCompleted = getFieldValue(results.DateCompleted);
                const needsStrategies = JSON.parse(
                  getFieldValue(results.NeedsStrategies)
                );
                const pastNeed = needsStrategies.filter(
                  (f) => f.NeedsStatement === item.NeedsStatement
                )[0];

                return (
                  <div key={sub.SubmissionId}>
                    {moment(dateCompleted).format("l")}:{" "}
                    {pastNeed.OutcomeProgress}
                  </div>
                );
              })}
          </>
        );

        return pastRatings;
      }

      return null;
    },
    [reviewSubmissions]
  );

  const handleStrategyCompleted = (
    e,
    strategies,
    stratIndex,
    needsStrategiesIndex
  ) => {
    const strat = strategies[stratIndex];
    strat.Completed = e.currentTarget.checked.toString();

    if (e.currentTarget.checked) {
      strat.DateCompleted = moment().format(moment.HTML5_FMT.DATE);
    } else {
      delete strat.DateCompleted;
    }

    e.currentTarget = {
      name: "Strategies",
      value: strategies,
      type: "array",
    };

    handleRowChange(e, needsStrategiesIndex, "NeedsStrategies");
  };

  const getCountOptions = () => {
    const options = [];

    for (let index = 0; index <= 10; index++) {
      options.push({ _id: index, name: index });
    }

    return options;
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              {pastSubmissionsForProgram.length === 0 ? (
                <>
                  <FormInput
                    name="CrisisPlanDate"
                    label={getFieldLabel(data.CrisisPlanDate)}
                    error={errors.CrisisPlanDate}
                    value={getFieldValue(data.CrisisPlanDate)}
                    onChange={handleChange}
                    identical={compare("CrisisPlanDate")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    type="date"
                    divClassName="clearfix"
                    labelClassName="fw-bold float-start me-2"
                  />
                  <FormInput
                    name="Participants"
                    label={getFieldLabel(data.Participants)}
                    error={errors.Participants}
                    value={getFieldValue(data.Participants)}
                    onChange={handleChange}
                    identical={compare("Participants")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  <div className="container border-top border-start border-dark">
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark bg-secondary-subtle py-2 text-center">
                        <div className="fs-6 fw-bold ">
                          {data.Symptoms["@initialHeader"] ||
                            "Initial Crisis Plan"}
                        </div>
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Symptoms"
                          label={getFieldLabel(data.Symptoms)}
                          error={errors.Symptoms}
                          value={getFieldValue(data.Symptoms)}
                          onChange={handleChange}
                          identical={compare("Symptoms")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="BriefHistory"
                          label={getFieldLabel(data.BriefHistory)}
                          error={errors.BriefHistory}
                          value={getFieldValue(data.BriefHistory)}
                          onChange={handleChange}
                          identical={compare("BriefHistory")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Triggers"
                          label={getFieldLabel(data.Triggers)}
                          error={errors.Triggers}
                          value={getFieldValue(data.Triggers)}
                          onChange={handleChange}
                          identical={compare("Triggers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="PotentialCrisis"
                          label={getFieldLabel(data.PotentialCrisis)}
                          error={errors.PotentialCrisis}
                          value={getFieldValue(data.PotentialCrisis)}
                          onChange={handleChange}
                          identical={compare("PotentialCrisis")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="ActionSteps"
                          label={getFieldLabel(data.ActionSteps)}
                          error={errors.ActionSteps}
                          value={getFieldValue(data.ActionSteps)}
                          onChange={handleChange}
                          identical={compare("ActionSteps")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="ResponsibleParties"
                          label={getFieldLabel(data.ResponsibleParties)}
                          error={errors.ResponsibleParties}
                          value={getFieldValue(data.ResponsibleParties)}
                          onChange={handleChange}
                          identical={compare("ResponsibleParties")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormSelect
                          name="YoungCount"
                          label={getFieldLabel(
                            formsyConfig.FormConfiguration?.[0].config[0]
                              .YoungCount
                          )}
                          error={errors.YoungCount}
                          value={getFieldValue(data.YoungCount)}
                          options={getCountOptions()}
                          onChange={handleChange}
                          identical={compare("YoungCount")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormSelect
                          name="AtRiskCount"
                          label={getFieldLabel(data.AtRiskCount)}
                          error={errors.AtRiskCount}
                          value={getFieldValue(data.AtRiskCount)}
                          options={getCountOptions()}
                          onChange={handleChange}
                          identical={compare("AtRiskCount")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
              {pastSubmissionsForProgram.length > 0 ? (
                <>
                  <div className="container-fluid border-start border-top border-dark mb-2">
                    <div className="row border-bottom border-dark">
                      <div className="col-6 border-end border-dark py-2">
                        <div>
                          <div className="fw-bold me-2">
                            {getFieldLabel(data.YouthName)}
                          </div>
                          <div>{getFieldValue(data.YouthName)}</div>
                        </div>
                      </div>
                      <div className="col-4 border-end border-dark py-2">
                        <FormInput
                          name="FileNumber"
                          label={getFieldLabel(data.FileNumber)}
                          error={errors.FileNumber}
                          value={getFieldValue(data.FileNumber)}
                          onChange={handleChange}
                          identical={compare("FileNumber")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                      <div className="col-2 border-end border-dark py-2">
                        <FormInput
                          name="DateCompleted"
                          label={getFieldLabel(data.DateCompleted)}
                          error={errors.DateCompleted}
                          value={getFieldValue(data.DateCompleted)}
                          onChange={handleChange}
                          identical={compare("DateCompleted")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col-6 border-end border-dark">
                        <div className="row h-100">
                          <div className="col-4 border-end border-dark py-2">
                            <div className="fw-bold me-2">
                              {getFieldLabel(data.DateOfBirth)}
                            </div>
                            <div>
                              {moment(getFieldValue(data.DateOfBirth)).format(
                                "l"
                              )}
                            </div>
                          </div>
                          <div className="col-8 py-2">
                            <FormInput
                              name="GuardianName"
                              label={getFieldLabel(data.GuardianName)}
                              error={errors.GuardianName}
                              value={getFieldValue(data.GuardianName)}
                              onChange={handleChange}
                              identical={compare("GuardianName")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                              labelClassName="fw-bold"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-3 border-end border-dark py-2">
                        <FormInput
                          name="GuardianPhone"
                          label={getFieldLabel(data.GuardianPhone)}
                          error={errors.GuardianPhone}
                          value={getFieldValue(data.GuardianPhone)}
                          onChange={handleChange}
                          identical={compare("GuardianPhone")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                      <div className="col-3 border-end border-dark py-2">
                        <FormInput
                          name="GuardianAddress"
                          label={getFieldLabel(data.GuardianAddress)}
                          error={errors.GuardianAddress}
                          value={getFieldValue(data.GuardianAddress)}
                          onChange={handleChange}
                          identical={compare("GuardianAddress")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Diagnosis"
                          label={getFieldLabel(data.Diagnosis)}
                          error={errors.Diagnosis}
                          value={getFieldValue(data.Diagnosis)}
                          onChange={handleChange}
                          identical={compare("Diagnosis")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Medications"
                          label={getFieldLabel(data.Medications)}
                          error={errors.Medications}
                          value={getFieldValue(data.Medications)}
                          onChange={handleChange}
                          identical={compare("Medications")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col-6 border-end border-dark py-2">
                        <FormRadioButton
                          name="PlanType"
                          label={getFieldLabel(data.PlanType)}
                          error={errors.PlanType}
                          value={getFieldValue(data.PlanType)}
                          options={JSON.parse(
                            data.PlanType["@options"].replaceAll("'", '"')
                          )}
                          onChange={handleChange}
                          identical={compare("PlanType")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col-2 border-end border-dark py-2">
                        <FormInput
                          name="PlanStartDate"
                          label={getFieldLabel(data.PlanStartDate)}
                          error={errors.PlanStartDate}
                          value={getFieldValue(data.PlanStartDate)}
                          onChange={handleChange}
                          identical={compare("PlanStartDate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          labelClassName="fw-bold"
                        />
                      </div>
                      <div className="col-4 border-end border-dark py-2">
                        <FormInput
                          name="PlanTargetDate"
                          label={getFieldLabel(data.PlanTargetDate)}
                          error={errors.PlanTargetDate}
                          value={getFieldValue(data.PlanTargetDate)}
                          onChange={handleChange}
                          identical={compare("PlanTargetDate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type="date"
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark bg-secondary-subtle py-2 text-center">
                        <div className="fs-6 fw-bold">
                          {data.FamilyVision["@header"]}
                        </div>
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col fw-bold border-end border-dark py-2">
                        {data.FamilyVision["@col1"]}
                      </div>
                      {getFieldValue(data.PlanType) === "Review" ||
                      getFieldValue(data.PlanType) === "Discharge" ? (
                        <div className="col-6 fw-bold border-end border-dark py-2">
                          {data.FamilyVision["@col2"]}
                        </div>
                      ) : null}
                    </div>
                    {data.FamilyVision["@value"]
                      ? JSON.parse(data.FamilyVision["@value"]).map(
                          (item, index) => (
                            <div className="row border-bottom border-dark">
                              <div className="col border-end border-dark py-2">
                                <div className="d-flex">
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "FamilyVision");
                                    }}
                                  />
                                  <div className="flex-fill">
                                    <FormInput
                                      name="Vision"
                                      value={item.Vision}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "FamilyVision"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                                {getFieldValue(data.PlanType) === "Review" ||
                                getFieldValue(data.PlanType) === "Discharge"
                                  ? renderFamilyVisionRatings(item)
                                  : null}
                              </div>
                              {getFieldValue(data.PlanType) === "Review" ||
                              getFieldValue(data.PlanType) === "Discharge" ? (
                                <div className="col-6 border-end border-dark py-2">
                                  <FormRadioButton
                                    name={`Rating-FamilyVision-${index}`}
                                    value={item.Rating}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(e, index, "FamilyVision");
                                    }}
                                    disabled={formDisabled}
                                    options={JSON.parse(
                                      data.FamilyVision[
                                        "@col2Options"
                                      ].replaceAll("'", '"')
                                    )}
                                  />
                                  <FormInput
                                    name="EndDate"
                                    label={data.FamilyVision["@col3"]}
                                    value={item.EndDate}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "FamilyVision");
                                    }}
                                    disabled={formDisabled}
                                    type="date"
                                  />
                                </div>
                              ) : null}
                            </div>
                          )
                        )
                      : null}
                    {!formDisabled ? (
                      <div className="row">
                        <div className="col border-end border-dark">
                          <button
                            className="btn btn-primary my-2"
                            onClick={() => {
                              handleAddRow("FamilyVision", {
                                IsEditable: true,
                              });
                            }}
                          >
                            {data.FamilyVision["@addLabel"]}
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {errors.FamilyVision ? (
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark">
                          <div className="alert alert-danger">
                            {errors.FamilyVision}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row border-bottom border-dark">
                      <div className="col fw-bold border-end border-dark py-2">
                        {data.TeamMission["@col1"]}
                      </div>
                      {getFieldValue(data.PlanType) === "Review" ||
                      getFieldValue(data.PlanType) === "Discharge" ? (
                        <div className="col-6 fw-bold border-end border-dark py-2">
                          {data.TeamMission["@col2"]}
                        </div>
                      ) : null}
                    </div>
                    {data.TeamMission["@value"]
                      ? JSON.parse(data.TeamMission["@value"]).map(
                          (item, index) => (
                            <div className="row border-bottom border-dark">
                              <div className="col border-end border-dark py-2">
                                <div className="d-flex">
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(index, "TeamMission");
                                    }}
                                  />
                                  <div className="flex-fill">
                                    <FormInput
                                      name="Mission"
                                      value={item.Mission}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "TeamMission"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                                {getFieldValue(data.PlanType) === "Review" ||
                                getFieldValue(data.PlanType) === "Discharge"
                                  ? renderTeamMissionRatings(item)
                                  : null}
                              </div>
                              {getFieldValue(data.PlanType) === "Review" ||
                              getFieldValue(data.PlanType) === "Discharge" ? (
                                <div className="col-6 border-end border-dark py-2">
                                  <FormRadioButton
                                    name={`Rating-TeamMission-${index}`}
                                    value={item.Rating}
                                    onChange={(e) => {
                                      e.currentTarget = {
                                        name: e.currentTarget.name.split(
                                          "-"
                                        )[0],
                                        value: e.currentTarget.value,
                                      };
                                      handleRowChange(e, index, "TeamMission");
                                    }}
                                    disabled={formDisabled}
                                    options={JSON.parse(
                                      data.TeamMission[
                                        "@col2Options"
                                      ].replaceAll("'", '"')
                                    )}
                                  />
                                  <FormInput
                                    name="EndDate"
                                    label={data.TeamMission["@col3"]}
                                    value={item.EndDate}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "TeamMission");
                                    }}
                                    disabled={formDisabled}
                                    type="date"
                                  />
                                </div>
                              ) : null}
                            </div>
                          )
                        )
                      : null}
                    {!formDisabled ? (
                      <div className="row">
                        <div className="col border-end border-dark">
                          <button
                            className="btn btn-primary my-2"
                            onClick={() => {
                              handleAddRow("TeamMission", {
                                IsEditable: true,
                              });
                            }}
                          >
                            {data.TeamMission["@addLabel"]}
                          </button>
                        </div>
                      </div>
                    ) : null}
                    {errors.TeamMission ? (
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark">
                          <div className="alert alert-danger">
                            {errors.TeamMission}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark pt-2">
                        <div className="fw-bold mb-2">
                          {getFieldLabel(data.IndividualStrengths)}
                        </div>
                        <div className="container border-top border-start border-dark">
                          <div className="row border-bottom border-dark">
                            <div className="col-4 fw-bold border-end border-dark py-2">
                              {data.IndividualStrengths["@col1"]}
                            </div>
                            <div className="col-8 fw-bold border-end border-dark py-2">
                              {data.IndividualStrengths["@col2"]}
                            </div>
                          </div>
                          {data.IndividualStrengths["@value"]
                            ? JSON.parse(
                                data.IndividualStrengths["@value"]
                              ).map((item, index) => (
                                <div className="row border-bottom border-dark">
                                  <div className="col-4 border-end border-dark py-2 d-flex">
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "IndividualStrengths"
                                        );
                                      }}
                                    />
                                    <div className="flex-fill">
                                      <FormInput
                                        name="Name"
                                        value={item.Name}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "IndividualStrengths"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="col-8 border-end border-dark py-2">
                                    <FormInput
                                      name="Strengths"
                                      value={item.Strengths}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "IndividualStrengths"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                              ))
                            : null}
                        </div>
                        {!formDisabled ? (
                          <div className="row">
                            <div className="col">
                              <button
                                className="btn btn-primary my-2"
                                onClick={() => {
                                  handleAddRow("IndividualStrengths", {
                                    IsEditable: true,
                                  });
                                }}
                              >
                                {data.IndividualStrengths["@addLabel"]}
                              </button>
                            </div>
                          </div>
                        ) : null}
                        {errors.IndividualStrengths ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.IndividualStrengths}
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark bg-secondary-subtle py-2 text-center">
                        <div className="fs-6 fw-bold ">
                          {data.Symptoms["@header"]}
                        </div>
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Symptoms"
                          label={getFieldLabel(data.Symptoms)}
                          error={errors.Symptoms}
                          value={getFieldValue(data.Symptoms)}
                          onChange={handleChange}
                          identical={compare("Symptoms")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="BriefHistory"
                          label={getFieldLabel(data.BriefHistory)}
                          error={errors.BriefHistory}
                          value={getFieldValue(data.BriefHistory)}
                          onChange={handleChange}
                          identical={compare("BriefHistory")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="Triggers"
                          label={getFieldLabel(data.Triggers)}
                          error={errors.Triggers}
                          value={getFieldValue(data.Triggers)}
                          onChange={handleChange}
                          identical={compare("Triggers")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="PotentialCrisis"
                          label={getFieldLabel(data.PotentialCrisis)}
                          error={errors.PotentialCrisis}
                          value={getFieldValue(data.PotentialCrisis)}
                          onChange={handleChange}
                          identical={compare("PotentialCrisis")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="ActionSteps"
                          label={getFieldLabel(data.ActionSteps)}
                          error={errors.ActionSteps}
                          value={getFieldValue(data.ActionSteps)}
                          onChange={handleChange}
                          identical={compare("ActionSteps")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark py-2">
                        <FormInput
                          name="ResponsibleParties"
                          label={getFieldLabel(data.ResponsibleParties)}
                          error={errors.ResponsibleParties}
                          value={getFieldValue(data.ResponsibleParties)}
                          onChange={handleChange}
                          identical={compare("ResponsibleParties")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          labelClassName="fw-bold"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="container-fluid border-start border-top border-dark mb-2">
                    <div className="row border-bottom border-dark">
                      <div className="col border-end border-dark bg-secondary-subtle py-2 text-center">
                        <div className="fs-6 fw-bold ">
                          {getFieldLabel(data.NeedsStrategies)}
                        </div>
                      </div>
                    </div>
                    {data.NeedsStrategies["@value"]
                      ? JSON.parse(data.NeedsStrategies["@value"]).map(
                          (item, index) => {
                            const strategies = item.Strategies
                              ? [...item.Strategies]
                              : [{ _id: "41957" }];

                            return (
                              <React.Fragment
                                key={`NeedsStrategies.${item._id}`}
                              >
                                <div className="row border-bottom border-dark">
                                  <div className="col-2 border-end border-dark py-2 text-center fw-bold">
                                    {data.NeedsStrategies["@col1"]}
                                    <br />
                                    <br />
                                    {index + 1}
                                    <br />
                                    <i
                                      className="far fa-trash-alt me-2 d-print-none"
                                      onClick={() => {
                                        handleDeleteRow(
                                          index,
                                          "NeedsStrategies"
                                        );
                                      }}
                                    />
                                  </div>
                                  <div className="col-5 border-end border-dark py-2">
                                    {item.IsEditable !== false ? (
                                      <FormTextArea
                                        name="NeedsStatement"
                                        value={item.NeedsStatement}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "NeedsStrategies"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.NeedsStatement
                                    )}
                                  </div>
                                  <div className="col-5 border-end border-dark py-2">
                                    <div className="row border-bottom border-dark">
                                      <div className="col py-2">
                                        <FormInput
                                          name="StartDate"
                                          label={data.NeedsStrategies["@col2"]}
                                          value={item.StartDate}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "NeedsStrategies"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="date"
                                        />
                                      </div>
                                    </div>
                                    <div className="row">
                                      <div className="col py-2">
                                        <FormInput
                                          name="EndDate"
                                          label={data.NeedsStrategies["@col3"]}
                                          value={item.EndDate}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "NeedsStrategies"
                                            );
                                          }}
                                          disabled={formDisabled}
                                          type="date"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {getFieldValue(data.PlanType) === "Review" ||
                                getFieldValue(data.PlanType) === "Discharge" ? (
                                  <div className="row border-bottom border-dark">
                                    <div className="col-2 border-end border-dark py-2">
                                      <div className="fw-bold">
                                        {data.NeedsStrategies["@col4"]}
                                      </div>
                                      <div>
                                        {data.NeedsStrategies["@col4Sub"]}
                                      </div>
                                    </div>
                                    <div className="col-5 border-end border-dark py-2">
                                      <FormRadioButton
                                        name={`NeedProgress-${index}`}
                                        value={item.NeedProgress}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                            value: e.currentTarget.value,
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "NeedsStrategies"
                                          );
                                        }}
                                        disabled={formDisabled}
                                        options={JSON.parse(
                                          data.NeedsStrategies[
                                            "@col4Options"
                                          ].replaceAll("'", '"')
                                        )}
                                      />
                                    </div>
                                    <div className="col-5 border-end border-dark py-2">
                                      {renderPastNeedProgressRating(item)}
                                    </div>
                                  </div>
                                ) : null}
                                <div className="row border-bottom border-dark">
                                  <div className="col-2 border-end border-dark py-2 fw-bold">
                                    {data.NeedsStrategies["@col5"]}
                                  </div>
                                  <div className="col-10 border-end border-dark py-2">
                                    <FormInput
                                      name="Outcome"
                                      value={item.Outcome}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "NeedsStrategies"
                                        );
                                      }}
                                      disabled={formDisabled}
                                    />
                                  </div>
                                </div>
                                {getFieldValue(data.PlanType) === "Review" ||
                                getFieldValue(data.PlanType) === "Discharge" ? (
                                  <div className="row border-bottom border-dark">
                                    <div className="col-2 border-end border-dark py-2 fw-bold">
                                      {data.NeedsStrategies["@col6"]}
                                    </div>
                                    <div className="col-5 border-end border-dark py-2">
                                      <FormRadioButton
                                        name={`OutcomeProgress-${index}`}
                                        value={item.OutcomeProgress}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                            value: e.currentTarget.value,
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "NeedsStrategies"
                                          );
                                        }}
                                        disabled={formDisabled}
                                        options={JSON.parse(
                                          data.NeedsStrategies[
                                            "@col6Options"
                                          ].replaceAll("'", '"')
                                        )}
                                      />
                                    </div>
                                    <div className="col-5 border-end border-dark py-2">
                                      {renderPastNeedsOutcomeRating(item)}
                                    </div>
                                  </div>
                                ) : null}
                                <div className="row border-bottom border-dark">
                                  <div className="col-7 border-end border-dark">
                                    <div className="fw-bold">
                                      {data.NeedsStrategies["@col7"]}
                                    </div>
                                    <div>
                                      {data.NeedsStrategies["@col7Sub"]}
                                    </div>
                                  </div>
                                  <div className="col-5 border-end border-dark py-2">
                                    <FormRadioButton
                                      name={`ProblemImpact-${index}`}
                                      value={item.ProblemImpact}
                                      onChange={(e) => {
                                        e.currentTarget = {
                                          name: e.currentTarget.name.split(
                                            "-"
                                          )[0],
                                          value: e.currentTarget.value,
                                        };
                                        handleRowChange(
                                          e,
                                          index,
                                          "NeedsStrategies"
                                        );
                                      }}
                                      disabled={formDisabled}
                                      options={JSON.parse(
                                        data.NeedsStrategies[
                                          "@col7Options"
                                        ].replaceAll("'", '"')
                                      )}
                                    />
                                  </div>
                                </div>
                                <div className="row border-bottom border-dark justify-content-center">
                                  <div className="col border-end border-dark text-center">
                                    <div className="fw-bold mb-1">
                                      {data.NeedsStrategies["@col8"]}
                                    </div>
                                    <div className="row row-cols-5">
                                      {JSON.parse(
                                        data.NeedsStrategies[
                                          "@col8Options"
                                        ].replaceAll("'", '"')
                                      )
                                        .slice(0, 5)
                                        .map((option) => (
                                          <div
                                            key={option._id}
                                            className="col text-start"
                                          >
                                            <FormCheckbox
                                              name={`${option._id}-${index}`}
                                              label={option.name}
                                              checked={
                                                item[option._id] === "true"
                                              }
                                              onChange={(e) => {
                                                e.currentTarget = {
                                                  ...e.currentTarget,
                                                  type: "checkbox",
                                                  name: e.currentTarget.name.split(
                                                    "-"
                                                  )[0],
                                                };
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "NeedsStrategies"
                                                );
                                              }}
                                              disabled={
                                                item.IsEditable === false ||
                                                formDisabled
                                              }
                                              divClassName="ms-3"
                                            />
                                          </div>
                                        ))}
                                    </div>
                                    <div className="row row-cols-9 justify-content-center">
                                      {JSON.parse(
                                        data.NeedsStrategies[
                                          "@col8Options"
                                        ].replaceAll("'", '"')
                                      )
                                        .slice(5)
                                        .map((option) => (
                                          <div
                                            key={option._id}
                                            className="col-3 text-start"
                                          >
                                            <FormCheckbox
                                              name={`${option._id}-${index}`}
                                              label={option.name}
                                              checked={
                                                item[option._id] === "true"
                                              }
                                              onChange={(e) => {
                                                e.currentTarget = {
                                                  ...e.currentTarget,
                                                  type: "checkbox",
                                                  name: e.currentTarget.name.split(
                                                    "-"
                                                  )[0],
                                                };
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "NeedsStrategies"
                                                );
                                              }}
                                              disabled={
                                                item.IsEditable === false ||
                                                formDisabled
                                              }
                                              divClassName="ms-3"
                                            />
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                                <div className="row border-bottom border-dark">
                                  <div className="col border-end border-dark py-1 fw-bold">
                                    {data.NeedsStrategies["@col9Header"]}
                                  </div>
                                </div>
                                <div className="row border-bottom border-dark">
                                  <div className="col border-end border-dark py-2">
                                    <div className="container border-dark border-top">
                                      <div className="row border-start border-bottom border-dark">
                                        <div className="col-10 border-end border-dark fw-bold py-1">
                                          {data.NeedsStrategies["@col9Label1"]}
                                        </div>
                                        <div className="col-2 border-end border-dark fw-bold py-1">
                                          {data.NeedsStrategies["@col9Label2"]}
                                        </div>
                                      </div>
                                      {strategies.map((strat, stratIndex) => (
                                        <div
                                          key={stratIndex}
                                          className="row border-start border-bottom border-dark"
                                        >
                                          <div className="col-10 border-end border-dark py-2">
                                            <div className="d-flex">
                                              <i
                                                className="far fa-trash-alt me-2 d-print-none"
                                                onClick={() => {
                                                  strategies.splice(
                                                    stratIndex,
                                                    1
                                                  );
                                                  const e = {
                                                    currentTarget: {
                                                      value: strategies,
                                                      type: "array",
                                                      name: "Strategies",
                                                    },
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "NeedsStrategies"
                                                  );
                                                }}
                                              />
                                              <FormInput
                                                name={`Strategy-${strat._id}`}
                                                value={strat.Strategy}
                                                onChange={(e) => {
                                                  strategies[
                                                    stratIndex
                                                  ].Strategy =
                                                    e.currentTarget.value;
                                                  e.currentTarget = {
                                                    name: "Strategies",
                                                    value: strategies,
                                                    type: "array",
                                                  };
                                                  handleRowChange(
                                                    e,
                                                    index,
                                                    "NeedsStrategies"
                                                  );
                                                }}
                                                disabled={formDisabled}
                                                divClassName="flex-fill"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-2 border-end border-dark py-2">
                                            <FormCheckbox
                                              name={`Completed-${stratIndex}`}
                                              checked={
                                                strat.Completed === "true"
                                              }
                                              onChange={(e) => {
                                                handleStrategyCompleted(
                                                  e,
                                                  strategies,
                                                  stratIndex,
                                                  index
                                                );
                                              }}
                                              disabled={
                                                item.IsEditable === false ||
                                                formDisabled
                                              }
                                              divClassName="ms-3"
                                            />
                                            {strat.Completed === "true"
                                              ? moment(
                                                  strat.DateCompleted
                                                ).format("l")
                                              : null}
                                          </div>
                                        </div>
                                      ))}
                                      <div className="row">
                                        <div className="col">
                                          {!formDisabled ? (
                                            <button
                                              className="btn btn-primary my-2"
                                              onClick={() => {
                                                strategies.push({
                                                  _id: cryptoRandomString(10),
                                                  Strategy: "",
                                                });
                                                const e = {
                                                  currentTarget: {
                                                    value: strategies,
                                                    type: "array",
                                                    name: "Strategies",
                                                  },
                                                };
                                                handleRowChange(
                                                  e,
                                                  index,
                                                  "NeedsStrategies"
                                                );
                                              }}
                                              // disabled={
                                              //   !strategies[strategies.length - 1]
                                              //     .Strategy
                                              // }
                                            >
                                              {
                                                data.NeedsStrategies[
                                                  "@col9AddLabel"
                                                ]
                                              }
                                            </button>
                                          ) : null}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          }
                        )
                      : null}
                  </div>
                  {errors.NeedsStrategies ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.NeedsStrategies}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary my-2"
                      onClick={() => {
                        handleAddRow("NeedsStrategies", {
                          IsEditable: true,
                          Strategies: [{}],
                        });
                      }}
                    >
                      {data.NeedsStrategies["@addLabel"]}
                    </button>
                  ) : null}
                  <FieldTable
                    name="TeamContacts"
                    commentsHandler={handleComments}
                  >
                    <div className="container-fluid border-top border-start border-dark mb-2">
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark bg-secondary-subtle py-2 text-center">
                          <div className="fs-6 fw-bold">
                            {getFieldLabel(data.TeamContacts)}
                          </div>
                        </div>
                      </div>
                      <div className="row border-bottom border-dark text-center fw-bold">
                        <div className="col border-end border-dark py-2">
                          Support Name
                        </div>
                        <div className="col border-end border-dark py-2">
                          Contact and Organization
                        </div>
                        <div className="col border-end border-dark py-2">
                          Role
                        </div>
                      </div>
                      {data.TeamContacts["@value"]
                        ? JSON.parse(data.TeamContacts["@value"]).map(
                            (item, index) => (
                              <div
                                key={`TeamContacts.${item._id}`}
                                className="row border-bottom border-dark"
                              >
                                <div className="col border-end border-dark py-2 d-flex">
                                  {item.IsEditable !== false ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(index, "TeamContacts");
                                        }}
                                      />
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "TeamContacts"
                                          );
                                        }}
                                      />
                                      <div className="flex-fill">
                                        <FormInput
                                          name="SupportName"
                                          value={item.SupportName}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "TeamContacts"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </div>
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(index, "TeamContacts");
                                        }}
                                      />
                                      {item.SupportName}
                                    </>
                                  )}
                                </div>
                                <div className="col border-end border-dark py-2">
                                  <FormInput
                                    name="Contact"
                                    value={item.Contact}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "TeamContacts");
                                    }}
                                    disabled={formDisabled}
                                  />
                                </div>
                                <div className="col border-end border-dark py-2">
                                  <FormInput
                                    name="Role"
                                    value={item.Role}
                                    onChange={(e) => {
                                      handleRowChange(e, index, "TeamContacts");
                                    }}
                                    disabled={formDisabled}
                                  />
                                </div>
                              </div>
                            )
                          )
                        : null}
                    </div>
                    {!formDisabled ? (
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("TeamContacts", { IsEditable: true });
                        }}
                      >
                        {data.TeamContacts["@addLabel"]}
                      </button>
                    ) : null}
                    {errors.TeamContacts ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TeamContacts}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </FieldTable>
                  {getFieldValue(data.PlanType) === "Discharge" ? (
                    <div className="container-fluid border-start border-top border-dark">
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark py-2 text-center bg-secondary-subtle ">
                          <div className="fs-6 fw-bold">
                            {data.SupportSummary["@header"]}
                          </div>
                        </div>
                      </div>
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark py-2">
                          <FormInput
                            name="SupportSummary"
                            label={getFieldLabel(data.SupportSummary)}
                            error={errors.SupportSummary}
                            value={getFieldValue(data.SupportSummary)}
                            onChange={handleChange}
                            identical={compare("SupportSummary")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            labelClassName="fw-bold"
                          />
                        </div>
                      </div>
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark py-2">
                          <FormInput
                            name="Recommendations"
                            label={getFieldLabel(data.Recommendations)}
                            error={errors.Recommendations}
                            value={getFieldValue(data.Recommendations)}
                            onChange={handleChange}
                            identical={compare("Recommendations")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            labelClassName="fw-bold"
                          />
                        </div>
                      </div>
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark py-2">
                          <FormSelect
                            name="YoungRemaining"
                            label={getFieldLabel(
                              formsyConfig.FormConfiguration?.[0].config[0]
                                .YoungRemaining
                            )}
                            error={errors.YoungRemaining}
                            value={getFieldValue(data.YoungRemaining)}
                            options={getCountOptions()}
                            onChange={handleChange}
                            identical={compare("YoungRemaining")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            labelClassName="fw-bold"
                          />
                        </div>
                      </div>
                      <div className="row border-bottom border-dark">
                        <div className="col border-end border-dark py-2">
                          <FormSelect
                            name="AtRiskRemaining"
                            label={getFieldLabel(data.AtRiskRemaining)}
                            error={errors.AtRiskRemaining}
                            value={getFieldValue(data.AtRiskRemaining)}
                            options={getCountOptions()}
                            onChange={handleChange}
                            identical={compare("AtRiskRemaining")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            labelClassName="fw-bold"
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYHFPlanOfCarev3;
